import React from 'react';
import Divider from '@mui/material/Divider';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import { MineModelDto, ProjectModule, useMineModelQuery } from 'providers/api';
import { useProject } from 'modules/project/providers/project';
import { useScenario } from 'modules/project/providers/scenario';
import { useAuthorisation } from 'providers/authorisation';
import { isPcUser } from 'utils/roles';
import { i18n } from 'i18n';
import { Model } from '../../../hooks/useModelState';
import { ISceneManagerContext } from '../../../hooks/SceneManagerProvider/sceneManagerContext';
import { StringNodesForm } from '../../../EditorPanelPage/components/StringNodesForm/StringNodesForm';
import { BetweenNodesForm } from '../../../EditorPanelPage/components/BetweenNodesForm/BetweenNodesForm';
import DraggableDialog from '../../../EditorPanelPage/components/DraggableDialog/DraggableDialog';
import ImportExportDialogs from '../../../EditorPanelPage/components/ImportExportDialogs/ImportExportDialogs';
import useDialogResize from '../../../EditorPanelPage/hooks/useDialogResize';
import useDialogPosition from '../../../EditorPanelPage/hooks/useDialogPosition';
import { navigateToScene } from '../../scene-utils';
import ToolbarSection from './ToolbarSection';
import ToolbarButton from './ToolbarButton';

interface ImportExportTabProps {
  model: Model;
  context: ISceneManagerContext;
}

const ImportExportTab = ({ model, context }: ImportExportTabProps) => {
  const [isAddNodeOpen, setIsAddNodeOpen] = React.useState(false);
  const [isAddStringOpen, setIsAddStringOpen] = React.useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = React.useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = React.useState(false);
  const { dialogSize, handleResizeStart } = useDialogResize();
  const { position: addNodeDialogPosition, updatePosition: updateAddNodeDialogPosition } = useDialogPosition({
    dialogSize,
    isOpen: isAddNodeOpen || isAddStringOpen,
  });
  const { state: { enabledModules } } = useProject();
  const stopeFillEnabled = enabledModules.includes(ProjectModule.StopeFill);
  const currentSceneKey = context.state.currentScene?.key;
  const projectId = model.project.entityId as string;
  const { activeScenarioId } = useScenario();
  const mineModelQuery = useMineModelQuery({ mineModelId: projectId, scenarioId: activeScenarioId ?? undefined });
  const authorisation = useAuthorisation();
  const isPc = isPcUser(authorisation.state.user.roles);
  const isAdminOrPc = authorisation.functions.isAdmin() || isPc;
  const canImportStopeShapes = stopeFillEnabled || isPc;

  const centreAddNodeDialog = () => updateAddNodeDialogPosition(window.innerWidth / 2 - 400, window.innerHeight / 2 - 300);

  const handleAddNodeToSpool = () => {
    centreAddNodeDialog();
    setIsAddNodeOpen(true);
  };

  const handleAddNodeFromCoordinates = () => {
    centreAddNodeDialog();
    setIsAddStringOpen(true);
  };

  const handleImport = () => setIsImportDialogOpen(true);

  const handleExport = () => setIsExportDialogOpen(true);

  const handleImportStopeShapes = () => navigateToScene('BULK_IMPORT_SOLIDS', { model, context });

  return (
    <>
      <ToolbarSection label={i18n._('Nodes')}>
        <ToolbarButton
          label={i18n._('Add to Spool')}
          tooltipTitle={i18n._('Add Node to Spool')}
          description={i18n._('Insert a new node into an existing spool by picking the spool and clicking the insertion point.')}
          icon={<AddCircleOutlineIcon fontSize="small" />}
          onClick={handleAddNodeToSpool}
          selected={isAddNodeOpen}
        />
        <ToolbarButton
          label={i18n._('Add at Coordinates')}
          tooltipTitle={i18n._('Add Node from Coordinates')}
          description={i18n._('Add one or more nodes at exact X / Y / Z coordinates with chosen pipe types and previous-node connections.')}
          icon={<AddLocationAltIcon fontSize="small" />}
          onClick={handleAddNodeFromCoordinates}
          selected={isAddStringOpen}
        />
      </ToolbarSection>
      <Divider orientation="vertical" flexItem />
      <ToolbarSection label={i18n._('Import / Export')}>
        <ToolbarButton
          label={i18n._('Import')}
          tooltipTitle={i18n._('Import from External')}
          description={i18n._('Import reticulation strings (DXF / DUF), a project, nodes or stope CSVs, stope shapes, or a tunnel profile.')}
          icon={<FileUploadIcon fontSize="small" />}
          onClick={handleImport}
          selected={currentSceneKey === 'BULK_IMPORT_SOLIDS'}
        />
        <ToolbarButton
          label={i18n._('Export')}
          tooltipTitle={i18n._('Export Reticulation')}
          description={i18n._('Export the current nodes and spools to a CSV file.')}
          icon={<FileDownloadIcon fontSize="small" />}
          onClick={handleExport}
        />
      </ToolbarSection>

      <DraggableDialog
        isOpen={isAddNodeOpen}
        title={i18n._('Add Node to Spool')}
        position={addNodeDialogPosition}
        dialogSize={dialogSize}
        onClose={() => setIsAddNodeOpen(false)}
        onDrag={(_e, data) => updateAddNodeDialogPosition(data.x, data.y)}
        onResizeStart={handleResizeStart}
      >
        <BetweenNodesForm
          model={model}
          project={model.project}
          closeDialog={() => setIsAddNodeOpen(false)}
        />
      </DraggableDialog>

      <DraggableDialog
        isOpen={isAddStringOpen}
        title={i18n._('Add Node(s) from Coordinates')}
        position={addNodeDialogPosition}
        dialogSize={dialogSize}
        onClose={() => setIsAddStringOpen(false)}
        onDrag={(_e, data) => updateAddNodeDialogPosition(data.x, data.y)}
        onResizeStart={handleResizeStart}
      >
        <StringNodesForm
          model={model}
          project={model.project}
          unitSystemPreference={model.project.unitSystemPreference}
          customUnitFields={model.project.customUnitFields}
          closeDialog={() => setIsAddStringOpen(false)}
          isAdminOrPc={isAdminOrPc}
        />
      </DraggableDialog>

      {mineModelQuery.data && (
        <ImportExportDialogs
          importOpen={isImportDialogOpen}
          exportOpen={isExportDialogOpen}
          onImportClose={() => setIsImportDialogOpen(false)}
          onExportClose={() => setIsExportDialogOpen(false)}
          onImportStopeShapes={canImportStopeShapes ? handleImportStopeShapes : undefined}
          model={model}
          project={model.project}
          mineModel={mineModelQuery.data as MineModelDto}
          mineModelQuery={mineModelQuery}
          isAdminOrPc={isAdminOrPc}
        />
      )}
    </>
  );
};

export default ImportExportTab;
